// ============================================================
// TMR Portal Admin — como um arquivo exportado se chama
// ============================================================
import type { ExportFormat } from './types';

/**
 * Names an exported file `<lista>-<AAAA-MM-DD>.<ext>` — `empresas-2026-08-27.xlsx`.
 *
 * The date is in the file name because these end up in a Downloads folder next to the last four exports
 * of the same list, and "empresas.xlsx (3)" tells nobody which one is current. It is ISO so the folder
 * sorts chronologically, which the Brazilian format would not.
 *
 * @param base Base name in PT-BR, no extension.
 * @param format Which file is being written.
 * @param on The day to stamp; defaults to today. Passed in by the tests, which cannot depend on today.
 */
export function exportFileName(base: string, format: ExportFormat, on: Date = new Date()): string {
  const day = [
    on.getFullYear(),
    String(on.getMonth() + 1).padStart(2, '0'),
    String(on.getDate()).padStart(2, '0'),
  ].join('-');

  return `${slug(base)}-${day}.${format}`;
}

/**
 * A file name that survives every operating system: accents folded, everything else that is not a letter,
 * digit or hyphen collapsed into one hyphen.
 *
 * The fold is `NFD` plus dropping the diacritic — `\p{Diacritic}` rather than a literal range of
 * combining marks, which is invisible in a diff and easy to mangle in an editor. Without the fold "São
 * Paulo" would slug to `s-o-paulo`, because the accent NFD splits off is not a letter.
 */
function slug(base: string): string {
  return base
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'export';
}
